import { Fragment } from "react";
import { QueryStatus, useQuery } from "@tanstack/react-query";

import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedCard } from "@/components/layout/locked-content";

import { message } from "@/lib/utils";
import { BrandGovernanceApiFactory, type CompetitorBenchmarkParams } from "@/apis/brand-os/brand-governance";
import type { BenchmarkStatItem, CompetitorBenchmarkData } from "@/schema/brand-os/brand-governance";

function benchmarkConfig(data: CompetitorBenchmarkData): BenchmarkStatItem[] {
  const benchmark = data.benchmark;

  const velocity = benchmark?.velocity;
  const videoProduction = benchmark?.video_production;
  const compliance = benchmark?.compliance;

  const brandCountVel = velocity?.brand_value?.count ?? 0;
  const leaderCountVel = velocity?.leader?.count ?? 0;

  const brandCountVp = videoProduction?.brand_value?.count ?? 0;
  const leaderCountVp = videoProduction?.leader?.count ?? 0;

  const brandScore = compliance?.brand_value?.score ?? 0;
  const leaderScore = compliance?.leader?.score ?? 0;

  return [
    {
      id: "velocity",
      title: "Velocity",
      value: String(brandCountVel),
      percentOfLeader: leaderCountVel === 0 ? 0 : Math.round((brandCountVel / leaderCountVel) * 100),
      leaderValue: String(leaderCountVel),
    },
    {
      id: "video_production",
      title: "Video production",
      value: String(brandCountVp),
      percentOfLeader: leaderCountVp === 0 ? 0 : Math.round((brandCountVp / leaderCountVp) * 100),
      leaderValue: String(leaderCountVp),
    },
    {
      id: "compliance",
      title: "Compliance",
      value: brandScore.toFixed(1),
      percentOfLeader: leaderScore === 0 ? 0 : Math.round((brandScore / leaderScore) * 100),
      leaderValue: leaderScore.toFixed(1),
    },
  ];
}

function StatsComponent({ status, benchmarkData }: { status: QueryStatus; benchmarkData?: CompetitorBenchmarkData | null }) {
  switch (status) {
    case "pending":
      return <StatsSkeleton />;
    case "error":
      return <LockedCard message={message} className="col-span-full" />;
    case "success":
      if (!benchmarkData?.competitors?.length) {
        return <LockedCard message={message} className="col-span-full" />;
      }
      return <StatsCards data={benchmarkData} />;
  }
}

export function Stats({ params }: { params: CompetitorBenchmarkParams }) {
  const { data, status } = useQuery({
    ...BrandGovernanceApiFactory.Queries.CompetitorBenchmark(params),
    enabled: Boolean(params.brand_name),
  });

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <StatsComponent status={status} benchmarkData={data?.data} />
    </div>
  );
}

function StatsContent({ item }: { item: BenchmarkStatItem }) {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3">
        <p className="text-sm text-muted-foreground">{item.title}</p>
        <p className="text-2xl font-bold text-destructive">{item.value}</p>
        <div className="flex items-center gap-2">
          <Progress value={item.percentOfLeader} className="h-2 flex-1 bg-muted" indicatorClassName="bg-destructive rounded-full" />
          <span className="w-10 shrink-0 text-right text-sm text-muted-foreground">{item.percentOfLeader}%</span>
        </div>
        <p className="text-sm text-muted-foreground">
          vs leader <span className="font-medium text-foreground">{item.leaderValue}</span>
        </p>
      </CardContent>
    </Card>
  );
}

function StatsCards({ data }: { data: CompetitorBenchmarkData }) {
  const benchmarkStats = benchmarkConfig(data);
  return (
    <Fragment>
      {benchmarkStats.map((item) => (
        <StatsContent key={item.id} item={item} />
      ))}
    </Fragment>
  );
}

function StatsSkeleton() {
  return (
    <Fragment>
      {Array.from({ length: 3 }, (_, i) => (
        <Card key={i}>
          <CardContent className="flex flex-col gap-3">
            <Skeleton className="h-4 w-20 rounded-md" />
            <Skeleton className="h-8 w-12 rounded-md" />
            <div className="flex items-center gap-2">
              <Skeleton className="h-2 min-w-0 flex-1 rounded-full" />
              <Skeleton className="h-4 w-10 shrink-0 rounded-md" />
            </div>
            <Skeleton className="h-4 w-24 rounded-md" />
          </CardContent>
        </Card>
      ))}
    </Fragment>
  );
}
